/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, ItemAvailability, SimpleProduct, AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    //IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxResources as IBuyboxExtentionResources,
    // IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, INodeProps, ITelemetryContent, Node, Module } from '@msdyn365-commerce-modules/utilities';
//, IModuleProps
import { reaction } from 'mobx';
import * as React from 'react';

import {
    IBuyboxProps as IBuyboxExtentionProps,
    IBuyboxProps,
    IBuyboxResources
} from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import { IBuyboxData } from './buybox.data';

import { IAppSettings, IProductList, ICoreContext } from '@msdyn365-commerce/core'; // getAsset
import {
    getAttributeByTechnicalName,
    getAttributesByTechnicalNamePrefix,
    AttributeValueWithTechnicalName
} from '../../../common/utilities/attributes-helpers';

import { isYes } from '../../../common/utilities/global-parameters.utils';

import { isProductsCategory } from '../../../common/config/category-constants';
import { checkIsAccessible, isProductVisibleForCustomer } from '../../../common/utilities/access-control';

import { convertProductListToSimpleItems, SimpleProductList } from '../../../common/utilities/product-collection-utilities';

import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';

import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';

import { checkVolumePricingValidity } from '../../../common/utilities/volume-pricing.utils';

import { handleTdsDocument } from '../../../common/config/tds-azure-function-url-helper';

const getBusinessPartnerId = async (actionContext: any): Promise<string | undefined> => {
    const businessPartner = await getCurrentAsync({ callerContext: actionContext });
    return businessPartner?.BusinessPartnerId;
};

/**
 * Composant pour afficher les produits reliés aux CULINARYPREP - Version ultra-simplifiée
 */
const RenderCulinaryPrepRelatedProducts: React.FC<{
    productList?: IProductList;
    context: ICoreContext;
    className?: string;
    config: IBuyboxProps<IBuyboxData>['config'];
    displayMode?: 'text' | 'images'; // Nouveau paramètre pour le mode d'affichage
}> = ({ productList, context, className = 'culinary-prep-related', config, displayMode = 'text' }) => {
    const products = convertProductListToSimpleItems(productList, context);

    if (products.length === 0) {
        return null;
    }

    // Mode texte (comportement actuel)
    if (displayMode === 'text') {
        return (
            <div className={className}>
                <SimpleProductList products={products} title={config.relatedProductsListTitle} className='culinary-prep-products' />
            </div>
        );
    }

    // Mode images (nouveau mode)
    return (
        <div className={`${className} ${className}--images`}>
            {config.relatedProductsListTitle && <h3 className='culinary-prep-products-title'>{config.relatedProductsListTitle}</h3>}
            <div className='culinary-prep-products culinary-prep-products--images'>
                {products.map((product, index) => (
                    <div key={product.id || index} className='culinary-prep-product-item culinary-prep-product-item--image'>
                        <a href={product.url} className={`${className}__link`}>
                            {product.PrimaryImageUrl && (
                                <div className='culinary-prep-product-thumbnail-container'>
                                    <img
                                        src={product.PrimaryImageUrl}
                                        alt={product.wbsItemName || product.name}
                                        className='culinary-prep-product-thumbnail'
                                        title={product.wbsItemName || product.name}
                                    />
                                </div>
                            )}
                            <div className='carousel-name'>{product.wbsItemName || product.name}</div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Composant pour afficher les autres packagings - Version ultra-simplifiée
 */
const RenderOtherPackagings: React.FC<{
    productList?: IProductList;
    context: ICoreContext;
    className?: string;
    config: IBuyboxProps<IBuyboxData>['config'];
    displayMode?: 'text' | 'images';
}> = ({ productList, context, className = 'other-packagings', config, displayMode = 'text' }) => {
    const products = convertProductListToSimpleItems(productList, context);

    if (products.length === 0) {
        return null;
    }

    // Mode texte (comportement actuel)
    if (displayMode === 'text') {
        return (
            <div className={className}>
                <SimpleProductList products={products} title={config.otherPackagingsListTitle} className='other-packagings-products' />
            </div>
        );
    }

    // Mode images (nouveau mode)
    return (
        <div className={`${className} ${className}--images`}>
            {config.otherPackagingsListTitle && <h3 className='other-packagings-title'>{config.otherPackagingsListTitle}</h3>}
            <div className='other-packagings-products other-packagings-products--images'>
                {products.map((product, index) => (
                    <div key={product.id || index} className='other-packagings-product-item other-packagings-product-item--image'>
                        <a href={product.url} className={`${className}__link`}>
                            <div className='other-packagings-product-thumbnail-container'>
                                {product.PrimaryImageUrl && (
                                    <img
                                        src={product.PrimaryImageUrl}
                                        alt={product.wbsItemName || product.name}
                                        className='other-packagings-product-thumbnail'
                                        title={product.wbsItemName || product.name}
                                    />
                                )}
                                {!product.PrimaryImageUrl && <div className='other-packagings-no-image-placeholder msc-empty_image'></div>}
                            </div>
                            <div className='carousel-name'>{product.wbsItemName || product.name}</div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * Quick Buy Group component.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param min - Min.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @param wbsSalesUnit - WBS Sales Unit.
 * @param addToCart - AddToCart.
 */
const QuickBuyGroup = ({
    quantityComponent,
    callbacks,
    props,
    state,
    extentionResources,
    quantityLimitsMessages,
    min,
    max,
    applyDefaultOrderSettings,
    telemetryContent,
    unitOfMeasure,
    wbsSalesUnit,
    addToCart
}: {
    quantityComponent: IBuyboxProductQuantityViewProps;
    callbacks: IBuyboxCallbacks;
    props: IBuyboxProps<IBuyboxData>;
    state: IBuyboxState;
    extentionResources: IBuyboxExtentionResources;
    quantityLimitsMessages: React.ReactNode;
    min: number | undefined;
    max: number | undefined;
    applyDefaultOrderSettings?: boolean;
    telemetryContent?: ITelemetryContent;
    unitOfMeasure?: React.ReactNode;
    wbsSalesUnit?: string;
    addToCart: IBuyboxAddToCartViewProps;
}) => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;
    const { resources } = props;
    const { quantity } = state;

    return (
        <div className='quick-buy-group'>
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {errors}
                </Node>
                <div className='quantity-and-add-to-cart'>
                    <IncrementalQuantity
                        id='ms-buybox__product-quantity-input'
                        min={min}
                        max={max}
                        applyDefaultOrderSettings={applyDefaultOrderSettings}
                        currentCount={quantity}
                        onChange={onChangeHandler(callbacks)}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                        minQuantityText={extentionResources.minQuantityText}
                        maxQuantityText={extentionResources.maxQuantityText}
                        telemetryContent={telemetryContent}
                    />
                    {addToCart && renderAddToCart(addToCart)}
                </div>
                <div className='wbs-sales-unit'>
                    {unitOfMeasure} <span> - {wbsSalesUnit}</span>
                </div>
                {quantityLimitsMessages}
            </Node>
        </div>
    );
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @param wbsSalesUnit - WBS Sales Unit.
 * @returns -- Returns.
 */
// const renderQuantity = (
//     quantityComponent: IBuyboxProductQuantityViewProps,
//     callbacks: IBuyboxCallbacks,
//     props: IBuyboxProps<IBuyboxData>,
//     state: IBuyboxState,
//     extentionResources: IBuyboxExtentionResources,
//     quantityLimitsMessages: React.ReactNode,

//     // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
//     min: number | undefined,
//     max: number | undefined,
//     applyDefaultOrderSettings?: boolean,
//     telemetryContent?: ITelemetryContent,
//     unitOfMeasure?: React.ReactNode,
//     wbsSalesUnit?: string
// ): JSX.Element => {
//     // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
//     const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

//     const { resources } = props;

//     const { quantity } = state;

//     return (
//         <Node {...ContainerProps}>
//             <Node {...LabelContainerProps}>
//                 {heading}
//                 {errors}
//             </Node>
//             <IncrementalQuantity
//                 id='ms-buybox__product-quantity-input'
//                 min={min}
//                 max={max}
//                 applyDefaultOrderSettings={applyDefaultOrderSettings}
//                 currentCount={quantity}
//                 onChange={onChangeHandler(callbacks)}
//                 inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
//                 decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
//                 incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
//                 minQuantityText={extentionResources.minQuantityText}
//                 maxQuantityText={extentionResources.maxQuantityText}
//                 telemetryContent={telemetryContent}
//             />
//             <div className='wbs-sales-unit'>
//                 {unitOfMeasure} <span> - {wbsSalesUnit}</span>
//             </div>
//             {quantityLimitsMessages}
//         </Node>
//     );
// };

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct,
    isShowWishlitButtonText: boolean = false
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    //const isShowWishlitButtonText = false;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param isShowWishlitButtonText - Flag to show wishlist button text.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    isShowWishlitButtonText: boolean = false
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product, isShowWishlitButtonText);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
// const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
//     // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
//     const { ContainerProps, errors, input } = shopSimilarItem;

//     return (
//         <Node {...ContainerProps}>
//             {errors}
//             {input}
//         </Node>
//     );
// };

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * RenderSpecialNeedsAttributes - Fonction pour afficher les attributs dont le nom commence par WBS_SN_.
 * @param productSpecificationData - Données des spécifications du produit.
 * @returns JSX.Element contenant les attributs spéciaux ou null si aucun.
 */
const renderSpecialNeedsAttributes = (
    productSpecificationData: AttributeValue[] | undefined,
    appSettings: IAppSettings
): JSX.Element | null => {
    if (!productSpecificationData || productSpecificationData.length === 0) {
        return null;
    }

    // Filtrer les attributs dont le technicalName commence par WBS_SN_
    // Variables booléennes (DataTypeValue: 6) : AttributeValueId 0 / 1
    const specialNeedsAttributes: AttributeValueWithTechnicalName[] = getAttributesByTechnicalNamePrefix(
        productSpecificationData,
        'WBS_SN_',
        appSettings
    );
    // console.log('✅ Attributs spéciaux:', specialNeedsAttributes);

    if (specialNeedsAttributes.length === 0) {
        return null;
    }

    return (
        <div className='wrapper-special-needs'>
            {specialNeedsAttributes.map((item, index) => {
                const { attribute, technicalName } = item;
                const className = `${technicalName} special-needs-attribute`;

                return (
                    <React.Fragment key={attribute.RecordId || index}>
                        {/* Unique critère de comparaison : l'attribut est une liste de strings traduites dans chaque langue sauf l'option "No" non traduite */}
                        {attribute.TextValue === 'No' ? '' : <div className={className}></div>}
                        {/* <div className='debug-info'>
                            <span className='attribute-name'>DEBUG {technicalName} :</span>
                            <span className='attribute-value'>{attribute.TextValue}</span>
                        </div> */}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

/**
 * RenderAttributes - Fonction de debug temporaire pour afficher les attributs du produit.
 * @param productSpecificationData - Données des spécifications du produit.
 * @returns JSX.Element de debug des attributs.
 */
// const renderAttributes = (productSpecificationData: any[] | undefined): JSX.Element | null => {
//     if (!productSpecificationData || productSpecificationData.length === 0) {
//         return (
//             <div style={{ display: 'none', border: '2px solid red', padding: '10px', margin: '10px', backgroundColor: '#ffe6e6' }}>
//                 <h3>DEBUG ATTRIBUTES - Aucun attribut trouvé</h3>
//             </div>
//         );
//     }

//     return (
//         <div style={{ display: 'none', border: '2px solid blue', padding: '10px', margin: '10px', backgroundColor: '#e6f3ff' }}>
//             <h3>DEBUG ATTRIBUTES ({productSpecificationData.length} attributs)</h3>
//             {productSpecificationData.map((attribute, index) => {
//                 const { DataTypeValue, Name, RecordId, TextValue, Keyname, ...otherFields } = attribute;

//                 // Affichage de la valeur selon le type
//                 let valueDisplay;
//                 if (DataTypeValue === 5) {
//                     // Type Text
//                     valueDisplay = (
//                         <span>
//                             <strong>TextValue:</strong> {TextValue || 'N/A'}
//                         </span>
//                     );
//                 } else {
//                     // Pour les autres types, afficher tous les champs disponibles sauf les champs de base
//                     const otherFieldNames = Object.keys(otherFields);
//                     valueDisplay = (
//                         <div>
//                             <div>
//                                 <strong>Autres champs disponibles:</strong> {otherFieldNames.join(', ')}
//                             </div>
//                             {otherFieldNames.map(fieldName => (
//                                 <div key={fieldName} style={{ marginLeft: '20px' }}>
//                                     <strong>{fieldName}:</strong> {JSON.stringify(otherFields[fieldName])}
//                                 </div>
//                             ))}
//                         </div>
//                     );
//                 }

//                 return (
//                     <div
//                         key={RecordId || index}
//                         style={{
//                             border: '1px solid gray',
//                             margin: '5px 0',
//                             padding: '8px',
//                             backgroundColor: '#f9f9f9'
//                         }}
//                     >
//                         <div>
//                             <strong>Name:</strong> {Name || 'N/A'}
//                         </div>
//                         <div>
//                             <strong>Keyname:</strong> {Keyname || 'N/A'}
//                         </div>
//                         <div>
//                             <strong>RecordId:</strong> {RecordId || 'N/A'}
//                         </div>
//                         <div>
//                             <strong>DataTypeValue:</strong> {DataTypeValue}
//                         </div>
//                         {valueDisplay}
//                     </div>
//                 );
//             })}
//         </div>
//     );
// };

/**
 * Affiche le tableau de prix dégressifs selon la relation client, avec présentation personnalisée.
 */
const renderVolumePricingTable = (
    config: IBuyboxProps<IBuyboxData>['config'],
    resources: IBuyboxExtentionResources & IBuyboxResources,
    volumePricingTableJson: string | undefined,
    customerHierarchyCode: string | undefined,
    priceGroup: string | undefined,
    wbsSalesUnit: string | undefined
): JSX.Element | null => {
    if (!volumePricingTableJson) return null;

    let pricingRows: any[] = [];
    try {
        pricingRows = JSON.parse(volumePricingTableJson);
    } catch {
        return <div style={{ color: 'red' }}>Erreur de parsing du tableau de prix dégressifs.</div>;
    }

    // 1. Chercher AT="C" & AR=customerHierarchyCode
    let filteredRows = pricingRows.filter(row => row.AT === 'C' && row.AR === customerHierarchyCode);

    // 2. Sinon AT="G" & AR=priceGroup
    if (filteredRows.length === 0 && priceGroup) {
        filteredRows = pricingRows.filter(row => row.AT === 'G' && row.AR === priceGroup);
    }

    // 3. Sinon AT="A"
    if (filteredRows.length === 0) {
        filteredRows = pricingRows.filter(row => row.AT === 'A');
    }

    if (filteredRows.length === 0) return null;

    // Tri par QF croissant
    filteredRows.sort((a, b) => a.QF - b.QF);

    // Génération des libellés de tranche
    const getRangeLabel = (rows: any[], idx: number): string => {
        const row = rows[idx];
        const isSingle = rows.length === 1;
        const isFirst = idx === 0;
        // const isLast = idx === rows.length - 1;
        if (isSingle && row.QT === 0) {
            return `≥ ${row.QF}`;
        }
        if (row.QT === 0) {
            return `≥ ${row.QF}`;
        }
        if (isFirst) {
            return `< ${row.QT}`;
        }
        return `${resources.priceTableFromLabel} ${row.QF} ${resources.priceTableToLabel} ${row.QT}`;
    };

    return (
        <>
            <p className='volume-pricing-sentence'>{config.degressivePriceSentence}</p>
            <div className='volume-pricing-table'>
                <table>
                    <thead>
                        <tr>
                            <th>{resources.priceTableQuantity}</th>
                            <th>
                                {resources.priceTablePrice}/{wbsSalesUnit}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.map((row, idx) => (
                            <tr key={idx}>
                                <td>{getRangeLabel(filteredRows, idx)}</td>
                                <td>{row.A} €</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        // description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        // shopSimilarLook,
        keyInPrice,
        // shopSimilarDescription,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs
    } = props;
    // console.log('🛠️ BuyboxView props:', props);
    // console.log('🛠️ BuyboxView data:', props.data);
    //console.log('🛠️ BuyboxView product:', props.data.product.result);
    // console.log('🛠️ BuyboxView productspecification:', props.data.productSpecificationData?.result);

    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    // Flag authentification : isAuthenticated
    const isAuthenticated = props.context.request.user.isAuthenticated;

    // Flag categorie racine (Products or Recipes) : productRootCategory
    let productRootCategory: string | undefined = undefined;
    const productRootCategories = props.data.productRootCategories?.result?.rootCategories || [];

    // Validation fonctionnelle
    // const hasMultipleRoots = productRootCategories.length > 1;
    const hasNoRoot = productRootCategories.length === 0;

    if (!hasNoRoot) {
        productRootCategory = productRootCategories[0]?.Name || '';
    }

    // if (hasMultipleRoots) {
    //     console.error('ERREUR: Le produit appartient à plusieurs arbres de catégories:', productRootCategories);
    // }
    // if (hasNoRoot) {
    //     console.error("ERREUR: Le produit n'appartient à aucun arbre de catégories.");
    // } else {
    //     productRootCategory = productRootCategories[0]?.Name || '';
    // }
    //console.log('✅ Product root category:', productRootCategory);

    // Attributes helpers
    const appSettings = props.context.app as IAppSettings;

    /* Product name */
    const wbsItemNameAttribute = getAttributeByTechnicalName(props.data.productSpecificationData?.result, 'WBS_ITEM_NAME', appSettings);
    const wbsItemName = wbsItemNameAttribute?.attribute.TextValue || '';

    /* Product Description */
    const wbsItemDescriptionAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_ITEM_DESCRIPTION',
        appSettings
    );
    const wbsItemDescription = wbsItemDescriptionAttribute?.attribute.TextValue || '';

    /* Product New flag string (Yes/No) */
    const wbsNewItemAttribute = getAttributeByTechnicalName(props.data.productSpecificationData?.result, 'WBS_NEW_ITEM', appSettings);
    const wbsNewItem: boolean = isYes(wbsNewItemAttribute?.attribute.TextValue);

    /* Product Sales Unit */
    const wbsSalesUnitAttribute = getAttributeByTechnicalName(props.data.productSpecificationData?.result, 'WBS_SALES_UNIT', appSettings);
    const wbsSalesUnit = wbsSalesUnitAttribute?.attribute.TextValue || '';

    /* Product Technical Datasheet Url */
    // const wbsTechnicalDatasheetUrlAttribute = getAttributeByTechnicalName(
    //     props.data.productSpecificationData?.result,
    //     'WBS_REJO_TDS_URL',
    //     appSettings
    // );
    // const wbsTechnicalDatasheetUrl = wbsTechnicalDatasheetUrlAttribute?.attribute.Name || '';

    /* Item categories */
    const wbsItemCategories = props.data.productRootCategories?.result?.productCategories;

    // Filtrer les catégories pour exclure celles qui sont dans productRootCategories
    const filteredCategories = wbsItemCategories?.filter(category => {
        const rootCategoryNames = new Set(productRootCategories.map(rootCat => rootCat.Name));
        return !rootCategoryNames.has(category.Name);
    });

    /* Recipe Display (Private or Public)*/
    const wbsCulinaryDisplayAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_DISPLAY',
        appSettings
    );
    const wbsCulinaryDisplay = wbsCulinaryDisplayAttribute?.attribute.TextValue?.toUpperCase() || '';

    /* Recipe Name */
    const wbsCulinaryNameAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_NAME',
        appSettings
    );
    const wbsCulinaryName = wbsCulinaryNameAttribute?.attribute.TextValue || '';

    /* Recipe Description */
    const wbsCulinaryDescriptionAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_DESCRIPTION',
        appSettings
    );
    const wbsCulinaryDescription = wbsCulinaryDescriptionAttribute?.attribute.TextValue || '';

    /* Recipe Ingredients */
    const wbsCulinaryIngredientsAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_INGREDIENTS',
        appSettings
    );
    const wbsCulinaryIngredients = wbsCulinaryIngredientsAttribute?.attribute.TextValue || '';

    /* Cooking Time */
    const wbsCookingTimeAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_COOKING_TIME',
        appSettings
    );
    const wbsCookingTime = wbsCookingTimeAttribute?.attribute.TextValue || '';

    /* Preparation Time */
    const wbsPreparationTimeAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_PREPARATION_TIME',
        appSettings
    );
    const wbsPreparationTime = wbsPreparationTimeAttribute?.attribute.TextValue || '';

    /* Recipe Steps */
    const wbsCulinaryStepsAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CULINA_PREP_STEPS',
        appSettings
    );
    const wbsCulinarySteps = wbsCulinaryStepsAttribute?.attribute.TextValue || '';

    /**** Volume Pricing (text : Yes or No) => obsolete : replaced by analysis of wbsVolumePricingTableJson with checkVolumePricingValidity()****/
    // const wbsVolumePricingAttribute = getAttributeByTechnicalName(
    //     props.data.productSpecificationData?.result,
    //     'WBS_VOLUME_PRICING',
    //     appSettings
    // );
    // const wbsVolumePricing: boolean = isYes(wbsVolumePricingAttribute?.attribute.TextValue);

    /**** Technical Datasheet URL ****/
    // const wbsTechnicalDatasheetUrlAttribute = getAttributeByTechnicalName(
    //     props.data.productSpecificationData?.result,
    //     'WBS_REJO_TDS_URL',
    //     appSettings
    // );
    // const wbsTechnicalDatasheetName = wbsTechnicalDatasheetUrlAttribute?.attribute.Name || '';
    // const wbsTechnicalDatasheetUrl = wbsTechnicalDatasheetUrlAttribute?.attribute.TextValue || '';

    /**** Made on order ****/
    const wbsMadeOnOrderAttribute = getAttributeByTechnicalName(props.data.productSpecificationData?.result, 'WBS_MTO_S002', appSettings);
    const wbsMadeOnOrder = wbsMadeOnOrderAttribute?.attribute.TextValue || '';

    /***** Access control****/

    const isAccessible = checkIsAccessible(productRootCategory, isAuthenticated, wbsCulinaryDisplay);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    // const customModuleProps: IModuleProps = {
    //     ...ModuleProps,
    //     tag: 'div'
    // };

    // const containerProps: INodeProps = {
    //     className: 'product-template',
    //     tag: 'div'
    // };

    // Remplace "Free" par "Price on request" dans les spans de prix
    React.useEffect(() => {
        const timer = setTimeout(() => {
            const wrapperPrice = document.querySelector('.wrapper-price');
            if (!wrapperPrice) return;

            const priceSpans = wrapperPrice.querySelectorAll('.msc-price__actual[itemprop="price"]');
            if (priceSpans.length > 0) {
                priceSpans.forEach(span => {
                    if (span.textContent === 'Free' || span.textContent === 'Gratuit' || span.textContent === 'Gratis') {
                        span.textContent = resources.priceOnRequest;
                    }
                });
            } else {
                const hasMscPrice = wrapperPrice.querySelector('.msc-price');
                if (!hasMscPrice && wrapperPrice.childElementCount === 0 && wrapperPrice.textContent?.trim() === '') {
                    const mscPriceDiv = document.createElement('div');
                    mscPriceDiv.className = 'msc-price';

                    const priceSpan = document.createElement('span');
                    priceSpan.className = 'msc-price__actual';
                    priceSpan.setAttribute('itemprop', 'price');
                    priceSpan.textContent = resources.priceOnRequest;

                    mscPriceDiv.appendChild(priceSpan);
                    wrapperPrice.appendChild(mscPriceDiv);
                }
            }
        }, 50); // 50ms

        return () => clearTimeout(timer);
    }, [price, resources.priceOnRequest]);

    // Récupération de la configuration productList pour autres packagings depuis Site Builder
    const otherPackagingsList = props.config.otherPackagingsList;

    // Récupération de la configuration productList pour produits liés à une recette depuis Site Builder
    const relatedProductsList = props.config.relatedProductsList;

    /**** Customer Specific product (text) ****/
    const wbsCustomerSpecificAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_CUST_HIERARCHY_SPECIFIC',
        appSettings
    );
    const wbsCustomerSpecific = wbsCustomerSpecificAttribute?.attribute.TextValue;
    // console.log('🆔 Product RecordId:', props.data.product.result?.RecordId, ' - WBS_CUST_HIERARCHY_SPECIFIC:', wbsCustomerSpecific);

    const [businessPartnerId, setBusinessPartnerId] = React.useState<string | undefined>(undefined);
    React.useEffect(() => {
        if (isAuthenticated) {
            getBusinessPartnerId(props.context.actionContext).then(id => {
                //console.log('BusinessPartnerId:', id);
                setBusinessPartnerId(id);
            });
        } else {
            setBusinessPartnerId(undefined);
        }
    }, [isAuthenticated, props.context.actionContext]);

    const isVisibleForCustomer = isProductVisibleForCustomer(wbsCustomerSpecific, businessPartnerId);

    // console.log('🔐 isNotCustomerSpecific:', isNotCustomerSpecific);
    // console.log('🔐 businessPartnerId:', businessPartnerId);

    // Récupération des données client pour algorithme d'affichage des trade agreements
    const [customer, setCustomer] = React.useState<any>(null);

    React.useEffect(() => {
        if (isAuthenticated) {
            const actionContext = props.context.actionContext;
            let customerId = isAuthenticated ? props.context.request.user.customerAccountNumber : undefined;
            if (process.env.NODE_ENV === 'development') {
                console.log('🏷️ Environnement de développement - CustomerID simulé');
                customerId = '7';
            }
            if (customerId) {
                const customerInput = new GetCustomerInput(props.context.request.apiSettings, customerId);

                getCustomer(customerInput, actionContext)
                    .then(data => {
                        setCustomer(data);
                        // console.log('✅ Customer data:', data);
                    })
                    .catch(error => {
                        // console.error('❌ Error fetching customer data:', error);
                        setCustomer(null);
                    });
            }
        }
    }, [isAuthenticated, props.context.actionContext, props.context.request.user.customerAccountNumber]);
    // console.log('🛠️ isAuthenticated:', isAuthenticated, ' - Customer:', customer);

    // // Composant de debug pour afficher la fiche client
    // const renderDebugCustomer = () => (
    //     <div style={{ border: '2px solid orange', padding: '10px', margin: '10px', backgroundColor: '#fffbe6' }}>
    //         <h3>DEBUG CUSTOMER DATA ACTION</h3>
    //         <div>
    //             <strong>getCustomer:</strong>
    //             <pre style={{ background: '#f4f4f4', padding: '8px', overflowX: 'auto' }}>{JSON.stringify(customer, null, 2)}</pre>
    //         </div>
    //     </div>
    // );

    // Dans BuyboxView, récupère l'attribut WBS_VOLUME_PRICING_TABLE
    const wbsVolumePricingTableAttribute = getAttributeByTechnicalName(
        props.data.productSpecificationData?.result,
        'WBS_VOLUME_PRICING_TABLE',
        appSettings
    );
    const wbsVolumePricingTableJson = wbsVolumePricingTableAttribute?.attribute.TextValue;
    // console.log('🛠️ WBS_VOLUME_PRICING_TABLE JSON:', wbsVolumePricingTableJson);

    /**** Volume Pricing Validity Check ****/
    const wbsVolumePricing: boolean = checkVolumePricingValidity(wbsVolumePricingTableJson, businessPartnerId, customer?.PriceGroup);

    React.useEffect(() => {
        if (productRootCategory && !isProductsCategory(productRootCategory) && !isAccessible) {
            window.location.href = props.context.request.user.signInUrl ?? '/_msdyn365/signin';
        }
    }, [productRootCategory, isAccessible]);

    // Effet pour masquer les messages avec la quantité maxQuantityForProductListLineItemtoHide (default 99999)
    React.useEffect(() => {
        const hideMaxQuantityMessages = () => {
            // Sélectionner tous les paragraphes de limites de quantité
            const quantityMessages = document.querySelectorAll('.ms-buybox__minmax_label_text');

            const maxQuantityForProductListLineItemtoHide = props.context.app.config.maxQuantityForProductListLineItemtoHide || 99999;

            quantityMessages.forEach(message => {
                // Vérifier si le texte contient la valeur de maxQuantityForProductListLineItemtoHide
                if (message.textContent && message.textContent.includes(maxQuantityForProductListLineItemtoHide.toString())) {
                    message.classList.add('hidden');
                }
            });
        };

        // Exécuter après un court délai pour s'assurer que le DOM est rendu
        const timer = setTimeout(hideMaxQuantityMessages, 100);

        return () => clearTimeout(timer);
    }, [quantityLimitsMessages]);

    // Inspecter les résultats des media locations
    // React.useEffect(() => {
    //     if (props.data.searchMediaLocations?.result) {
    //         console.log('🎬 Media locations résultats:', props.data.searchMediaLocations.result);
    //     }
    // }, [props.data.searchMediaLocations?.result]);

    const [open, setOpen] = React.useState(false);
    const [popupMessage, setPopupMessage] = React.useState('');
    const [popupAction, setPopupAction] = React.useState('');

    const showPopup = (type: 'warning' | 'info' | 'error', action: string) => {
        setOpen(true);
    };
    const handlePopupMessage = (message: string) => {
        setPopupMessage(message);
    };
    const handlePopupAction = (action: string) => {
        setPopupAction(action);
    };

    React.useEffect(() => {
        // Conteneur du bloc d’erreur du Buybox (adaptable selon ton markup)
        const container = document.querySelector('.ms-buybox__add-to-cart-container') || document.body;
        if (!container) return;

        // Mapping des messages à remplacer (original -> remplacement)
        const replacements: Record<string, string> = {
            // EN
            'Zero price is not allowed for selected product. Enter a different price and try again.':
                'Something might be wrong. Please contact customer service.',
            // FR
            "Le prix zéro n'est pas autorisé pour le produit sélectionné. Entrez un autre prix et réessayez.":
                'Il y a peut-être un problème. Veuillez contacter le service client.',
            // NL
            'Nulprijs is niet toegestaan voor geselecteerde product. Voer een andere prijs in en probeer het opnieuw.':
                'Er gaat mogelijks iets mis. Gelieve contact op te nemen met de klantendienst.'
        };

        // Normalise le texte pour comparer proprement
        const normalize = (s: string) => s.trim();

        const handleErrors = () => {
            const lines = Array.from(
                container.querySelectorAll('.msc-alert.msc-alert-danger .msc-alert__line:not(.dyn-replaced-error)')
            ) as HTMLElement[];

            lines.forEach(line => {
                const current = normalize(line.textContent || '');
                const key = Object.keys(replacements).find(k => normalize(k) === current);
                if (key) {
                    line.textContent = replacements[key];
                    line.classList.add('dyn-replaced-error');
                }
            });
        };

        const observer = new MutationObserver(() => handleErrors());
        observer.observe(container, { subtree: true, childList: true, characterData: true });

        // Première exécution
        handleErrors();

        return () => observer.disconnect();
    }, []);

    const baseImageUrl = props.context.request.apiSettings.baseImageUrl.endsWith('/')
        ? props.context.request.apiSettings.baseImageUrl.slice(0, -1)
        : props.context.request.apiSettings.baseImageUrl;

    if (isVisibleForCustomer) {
        return isProductsCategory(productRootCategory) || !productRootCategory ? (
            <>
                {open && (
                    <div className='tds-modal tds-modal__backdrop' role='dialog' aria-modal='true'>
                        <div className='tds-modal__content'>
                            <button className='tds-modal__close' onClick={() => setOpen(false)} aria-label='Close'>
                                ×
                            </button>
                            <div className='tds-modal__body'>{popupMessage}</div>
                            {popupAction && (
                                <a className='tds-modal__action' href={popupAction} target='_blank' rel='noopener noreferrer'>
                                    Open Link
                                </a>
                            )}
                        </div>
                    </div>
                )}
                <div className={`product-template container ${wbsNewItem ? 'new-item' : ''}`}>
                    <Module {...ModuleProps}>
                        <Node {...MediaGalleryContainerProps}>
                            {props.mediaGallery}
                            {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                            {wbsNewItem && <div className='flag-new'>{props.config.newItemLabel}</div>}
                        </Node>
                        <Node {...ProductInfoContainerProps}>
                            {catalogs && catalogs[0].Name}
                            <div className='top-ctas-and-flags'>
                                <button
                                    onClick={() => {
                                        const shareData = {
                                            title: `Solina recipe - ${wbsCulinaryName}`,
                                            text: props.config.shareText,
                                            url: props.context.request.url.requestUrl.href
                                        };

                                        if (navigator.share) {
                                            navigator
                                                .share(shareData)
                                                .then(() => console.log('Partage réussi'))
                                                .catch(error => console.log('Erreur lors du partage:', error));
                                        } else {
                                            // Fallback : copier l'URL dans le presse-papier
                                            if (navigator.clipboard) {
                                                navigator.clipboard
                                                    .writeText(shareData.url)
                                                    .then(() => alert('Lien copié dans le presse-papier'))
                                                    .catch(() => alert('Impossible de copier le lien'));
                                            } else {
                                                // Fallback ultime pour navigateurs plus anciens
                                                const textArea = document.createElement('textarea');
                                                textArea.value = shareData.url;
                                                document.body.appendChild(textArea);
                                                textArea.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(textArea);
                                                alert('Lien copié dans le presse-papier');
                                            }
                                        }
                                    }}
                                >
                                    <img width='23' height='23' src={`${baseImageUrl}icon-share.png`} alt='Share' />
                                </button>
                            </div>
                            <div className='wrapper-quick-buy'>
                                <div className='quick-buy-header'>
                                    <div className='quick-buy-title'>
                                        {wbsItemName ? <h1>{wbsItemName}</h1> : title}
                                        <Node className='msc-buybox__ratings-section'>
                                            <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                                            {rating}
                                        </Node>
                                    </div>
                                    <Node className='msc-buybox__price-section'>
                                        {/* Afficher le prix seulement si l'utilisateur est authentifié */}
                                        {isAccessible && (
                                            <>
                                                <div className='msc-buybox__price-section-text'>{props.resources.priceText}</div>
                                                <div className='wrapper-price'>
                                                    {price}
                                                    {wbsVolumePricing ? (
                                                        <img
                                                            className='msc-buybox__volume-pricing-icon'
                                                            src={`${baseImageUrl}icon-degressive.png`}
                                                            alt='Volume Pricing'
                                                        />
                                                    ) : null}
                                                </div>
                                            </>
                                        )}
                                    </Node>
                                </div>
                                <div className='quick-buy-actions'>
                                    <Node className='msc-buybox__bulk-purchase-section'>
                                        <div className='msc-buybox__bulk-purchase-button-text'>
                                            {/* Afficher la section quantité/achat en gros seulement si l'utilisateur est authentifié */}
                                            {/* {isAccessible &&
                                            quantity &&
                                            renderQuantity(
                                                quantity,
                                                callbacks,
                                                props,
                                                state,
                                                resources,
                                                quantityLimitsMessages,
                                                min,
                                                max,
                                                applyDefaultOrderSettings,
                                                telemetryContent,
                                                unitOfMeasure,
                                                wbsSalesUnit
                                            )} */}
                                            {isAccessible && quantity && addToCart && (
                                                <QuickBuyGroup
                                                    quantityComponent={quantity}
                                                    callbacks={callbacks}
                                                    props={props}
                                                    state={state}
                                                    extentionResources={resources}
                                                    quantityLimitsMessages={quantityLimitsMessages}
                                                    min={min}
                                                    max={max}
                                                    applyDefaultOrderSettings={applyDefaultOrderSettings}
                                                    telemetryContent={telemetryContent}
                                                    unitOfMeasure={unitOfMeasure}
                                                    wbsSalesUnit={wbsSalesUnit}
                                                    addToCart={addToCart}
                                                />
                                            )}
                                            {isAccessible && props.bulkPurchaseLink}
                                        </div>
                                    </Node>
                                </div>
                                {wbsVolumePricing &&
                                    wbsVolumePricingTableJson &&
                                    checkVolumePricingValidity(wbsVolumePricingTableJson, businessPartnerId, customer?.PriceGroup) &&
                                    renderVolumePricingTable(
                                        props.config,
                                        props.resources,
                                        wbsVolumePricingTableJson,
                                        businessPartnerId,
                                        customer?.PriceGroup,
                                        wbsSalesUnit
                                    )}
                            </div>
                            <Node className='msc-buybox__description-section'>
                                <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                                {wbsItemDescription}
                            </Node>
                            <div className='msc-buybox__special-needs'>
                                {renderSpecialNeedsAttributes(props.data.productSpecificationData?.result, appSettings)}
                            </div>
                            {/* {isAccessible && wbsTechnicalDatasheetUrl !== '' && (
                                <a
                                    href={wbsTechnicalDatasheetUrl}
                                    target='_blank'
                                    className='msc-buybox__TDS-link'
                                    title={wbsTechnicalDatasheetName}
                                >
                                    {wbsTechnicalDatasheetName}
                                </a>
                            )} */}
                            {isAccessible && (
                                <a
                                    href='#'
                                    target='_blank'
                                    className='msc-buybox__TDS-link'
                                    title={resources.tdsDownloadRequestMessage}
                                    onClick={event => {
                                        event.preventDefault();
                                        handleTdsDocument(
                                            appSettings,
                                            props.data.product.result?.ItemId ?? '', //props.data.product.result?.RecordId ?? 0,
                                            props.context.request.locale,
                                            showPopup,
                                            handlePopupMessage,
                                            handlePopupAction,
                                            'direct',
                                            resources.tdsUnavailableMessage,
                                            resources.tdsConfigurationErrorMessage,
                                            resources.tdsDocumentReadyMessage
                                        );
                                    }}
                                >
                                    {resources.tdsDownloadRequestMessage}
                                </a>
                            )}
                            {configure && renderConfigure(configure)}
                            {keyInPrice && renderKeyInPrice(keyInPrice)}
                            {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                            {inventoryLabel}
                            {/* Afficher le bouton d'ajout au panier seulement si l'utilisateur est authentifié */}
                            {findInStore && renderFindInStore(findInStore)}
                            {productComparisonButton}
                            {findInStore?.productPickupOptionList ? (
                                <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                            ) : null}
                            {_renderSocialShare(props.slots && props.slots.socialShare)}
                            {/* <div className='msc-buybox__description-text'>{props.resources.shopText}</div> */}
                            {/* <Node className='msc-buybox__shop-description'>
                        {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                        {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                        </Node> */}
                            <div className='msc-buybox__functions'>
                                {addToWishlist && renderAddToWishlist(props, state, callbacks, true)}
                                {isAccessible && addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                            </div>
                            {wbsMadeOnOrder && wbsMadeOnOrder !== '-' && <div className='msc-buybox__made-on-order'>{wbsMadeOnOrder}</div>}
                            <div className='other-packagings-wrapper'>
                                <RenderOtherPackagings
                                    productList={otherPackagingsList}
                                    context={props.context}
                                    className='other-packagings-module'
                                    config={props.config}
                                    displayMode='images'
                                />
                            </div>
                        </Node>
                    </Module>
                </div>
                <div className='wrapper-product-specification'>
                    <div className='container'>
                        {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                            <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                        ) : null}
                        {/* DEBUG TEMPORAIRE - Affichage des attributs */}
                        {/* {renderAttributes(props.data.productSpecificationData?.result)} */}
                    </div>
                </div>
                {/* <div className='container'>{renderDebugPricesTable()}</div> */}
                {/* <div className='container'>{renderDebugCustomer()}</div> */}
            </>
        ) : (
            <div className={`recipe-template ${isAccessible ? '' : 'guest-only'}`}>
                <div className='wrapper-recipe-header'>
                    <div className='ms-buybox container'>
                        <Node {...MediaGalleryContainerProps}>
                            {props.mediaGallery}
                            {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                        </Node>
                        <div className='recipe-header ms-buybox__content'>
                            <div className='header-ctas'>
                                <div className='print-recipe'>
                                    <button onClick={() => window.print()}>
                                        <img width='23' height='23' src={`${baseImageUrl}icon-print.png`} alt='Print' />
                                    </button>
                                </div>
                                <div className='share-recipe'>
                                    <button
                                        onClick={() => {
                                            const shareData = {
                                                title: `Solina recipe - ${wbsCulinaryName}`,
                                                text: props.config.shareText,
                                                url: props.context.request.url.requestUrl.href
                                            };

                                            if (navigator.share) {
                                                navigator
                                                    .share(shareData)
                                                    .then(() => console.log('Partage réussi'))
                                                    .catch(error => console.log('Erreur lors du partage:', error));
                                            } else {
                                                // Fallback : copier l'URL dans le presse-papier
                                                if (navigator.clipboard) {
                                                    navigator.clipboard
                                                        .writeText(shareData.url)
                                                        .then(() => alert('Lien copié dans le presse-papier'))
                                                        .catch(() => alert('Impossible de copier le lien'));
                                                } else {
                                                    // Fallback ultime pour navigateurs plus anciens
                                                    const textArea = document.createElement('textarea');
                                                    textArea.value = shareData.url;
                                                    document.body.appendChild(textArea);
                                                    textArea.select();
                                                    document.execCommand('copy');
                                                    document.body.removeChild(textArea);
                                                    alert('Lien copié dans le presse-papier');
                                                }
                                            }
                                        }}
                                    >
                                        <img width='23' height='23' src={`${baseImageUrl}icon-share.png`} alt='Share' />
                                    </button>
                                </div>
                            </div>
                            {wbsCulinaryName ? <h1>{wbsCulinaryName}</h1> : title}
                            {isAccessible && (
                                <>
                                    <div className='recipe-categories'>
                                        <ul>
                                            {filteredCategories?.map(category => (
                                                <li key={category.RecordId}>{category.Name}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className='recipe-description'>
                                        <p>{wbsCulinaryDescription}</p>
                                    </div>
                                    <div className='related-products'>
                                        <RenderCulinaryPrepRelatedProducts
                                            productList={relatedProductsList}
                                            context={props.context}
                                            className='recipe-culinary-prep-related'
                                            config={props.config}
                                            displayMode='text'
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isAccessible && (
                    <div className='wrapper-recipe-info container'>
                        <div className='recipe-ingredients'>
                            <h2>
                                <img width='40' height='40' src={`${baseImageUrl}icon-ingredients.png`} alt='Ingredients' />
                                {props.config.titleIngredients}
                            </h2>
                            <div className='recipe-ingredients-list' dangerouslySetInnerHTML={{ __html: wbsCulinaryIngredients }} />
                            {/* <div className='related-products'>
                                <RenderCulinaryPrepRelatedProducts
                                    productList={relatedProductsList}
                                    context={props.context}
                                    className='recipe-culinary-prep-related'
                                    config={props.config}
                                    displayMode='images'
                                />
                            </div> */}
                        </div>
                        <div className='recipe-steps'>
                            <div className='recipe-steps-header'>
                                <h2>
                                    <img width='33' height='40' src={`${baseImageUrl}icon-preparation.png`} alt='Preparation' />
                                    {props.config.titleInstructions}
                                </h2>
                                <div className='recipe-timings'>
                                    <div className='preparation-time'>
                                        {wbsPreparationTime ? props.config.titlePreparationTime + ' : ' + wbsPreparationTime : ''}
                                    </div>
                                    <div className='cooking-time'>
                                        {wbsCookingTime ? props.config.titleCookingTime + ' : ' + wbsCookingTime : ''}
                                    </div>
                                </div>
                            </div>
                            <div className='wrapper-recipe-steps-list'>
                                <div className='recipe-steps-list' dangerouslySetInnerHTML={{ __html: wbsCulinarySteps }} />
                                <span className='recipe-disclaimer'>{props.config.allergensDisclaimer}</span>
                            </div>
                        </div>
                    </div>
                )}
                {!isAccessible && (
                    <div className='container access-denied'>
                        <h2>Restricted area</h2>
                    </div>
                )}
                <div>
                    {/* DEBUG TEMPORAIRE - Affichage des attributs */}
                    {/* {renderAttributes(props.data.productSpecificationData?.result)} */}
                </div>
            </div>
        );
    }

    return (
        <div className='container'>
            <h1>This product is unavailable for your account</h1>
        </div>
    );
};

export default BuyboxView;
